import { Navigate, Outlet } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'
import { wrongTenantRedirect } from '@/lib/tenantHost'
import { ExternalRedirect } from './TenantRoutes'
import { ForcedPasswordChange } from '@/features/auth/components/ForcedPasswordChange'

/**
 * Renders child routes only when the user is authenticated; otherwise redirects to login.
 * With tenant subdomains on, the app only runs on the user's own organization's
 * subdomain: the session cookie is shared across subdomains, so someone signed
 * in to Acme who opens globex.kaamco.com is sent back to acme.kaamco.com.
 */
export function PrivateRoute(): React.ReactElement {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const organizationSlug = useAuthStore((s) => s.user?.organizationSlug)
  const mustChangePassword = useAuthStore((s) => s.user?.mustChangePassword === true)
  const inSupportSession = useAuthStore((s) => Boolean(s.user?.supportSession))

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const redirect = wrongTenantRedirect(organizationSlug)
  if (redirect) {
    return <ExternalRedirect to={redirect} />
  }

  // An admin-set (temporary) password must be replaced before anything else.
  // A support session views as the user and is read-only, so it is let through.
  if (mustChangePassword && !inSupportSession) {
    return <ForcedPasswordChange />
  }

  return <Outlet />
}
